/**
 * Lightweight enabled-games / enabled-providers count hooks for the lobby.
 *
 * These two counts feed the "HUGE GAME COLLECTION" card and the providers-row
 * kicker ("1,234 games across 12 providers"). They are independent of the
 * catalog and of each other, so they run as their own parallel cached queries
 * rather than serial mount-time effects.
 *
 * The underlying service methods already swallow errors and resolve to 0, so
 * these queries effectively never reject. They are kept fresh for a while
 * (counts move slowly) via a longer staleTime override.
 */

import { useQuery } from "@tanstack/react-query";
import casinoService from "../../../services/casinoService";
import { queryKeys } from "../../../shared/data/queryKeys";
import { normalizeQueryError } from "../../../shared/data/errors";

const COUNT_STALE_TIME = 5 * 60 * 1000; // counts change slowly; 5 min is plenty

export function useEnabledGamesCount() {
  return useQuery({
    queryKey: queryKeys.games.enabledGamesCount(),
    queryFn: async () => {
      try {
        const count = await casinoService.getEnabledGamesCount();
        return typeof count === "number" ? count : 0;
      } catch (err) {
        throw normalizeQueryError(err, "Failed to load games count");
      }
    },
    staleTime: COUNT_STALE_TIME,
  });
}

export function useEnabledProvidersCount() {
  return useQuery({
    queryKey: queryKeys.games.enabledProvidersCount(),
    queryFn: async () => {
      try {
        const count = await casinoService.getEnabledProvidersCount();
        return typeof count === "number" ? count : 0;
      } catch (err) {
        throw normalizeQueryError(err, "Failed to load providers count");
      }
    },
    staleTime: COUNT_STALE_TIME,
  });
}
